import { QueryClient } from "@tanstack/react-query";

import {
  ALL_USERS_KEY,
  COMMS_STATUSES,
  MAX_CONTACTS_ATTEMPTED,
  FINALIST_STATUSES,
  SUBMISSION_STATUSES,
} from "@/lib/constants";
import { jobs as jobsLib } from "@/lib/jobs";
import { cycleColumnSort } from "@/lib/sort";
import { stevieRoute } from "@/lib/stevieRoute";
import {
  CandidacyStatus,
  DashboardData,
  DashboardJob,
  DashboardSelectedUser,
  DashboardSortKey,
  DashboardSortState,
  JobDifficulty,
  JobPriority,
  MinimalCandidacy,
  SortOrder,
} from "@/types";

interface SnapshotCandidacy {
  id: string;
  status: CandidacyStatus;
  contactsAttempted: number | null;
  nextActionAt: string | null;
  stafferAgentActive: boolean;
}

// Columns whose first click sorts ascending (A→Z, or lowest rank first).
// Every other column leads descending (highest first).
const ASC_LEADING_SORT_KEYS: DashboardSortKey[] = [
  "jobs",
  "staffer",
  "difficulty",
  "bottleneck",
];

// Higher number = higher priority, so a descending first click floats High.
const PRIORITY_RANK: Record<JobPriority, number> = {
  [JobPriority.HIGH]: 3,
  [JobPriority.MEDIUM]: 2,
  [JobPriority.LOW]: 1,
  [JobPriority.BACKBURNERED]: 0,
};

const DIFFICULTY_RANK: Record<JobDifficulty, number> = {
  [JobDifficulty.EASY]: 1,
  [JobDifficulty.MODERATE]: 2,
  [JobDifficulty.DIFFICULT]: 3,
  [JobDifficulty.VERY_DIFFICULT]: 4,
};

export const dashboard = {
  jobPipelinePath: (job: DashboardJob): string =>
    `/companies/${job.companyId}/jobs/${job.id}?t=pipeline`,

  jobSearchPath: (job: DashboardJob): string =>
    `/companies/${job.companyId}/jobs/${job.id}?t=search-v2`,

  // A candidacy belongs to the selected view when everyone is selected, or when
  // the staffer owns it (orphans with no owner show for everyone), mirroring the
  // backend's per-user scoping now that the dashboard fetches all users at once.
  candidacyBelongsToView: (
    candidacy: { ownerUserIds: number[] },
    selectedUserId: DashboardSelectedUser,
  ): boolean =>
    selectedUserId === ALL_USERS_KEY ||
    candidacy.ownerUserIds.length === 0 ||
    candidacy.ownerUserIds.includes(selectedUserId),

  // A manager oversees the whole job, so a job they manage without owning its
  // reqs reads like the All view — every candidacy on it counts.
  viewUserForJob: (
    job: DashboardJob,
    selectedUserId: DashboardSelectedUser,
  ): DashboardSelectedUser =>
    selectedUserId !== ALL_USERS_KEY &&
    !job.ownerUserIds.includes(selectedUserId) &&
    job.managerUserIds.includes(selectedUserId)
      ? ALL_USERS_KEY
      : selectedUserId,

  countCommsToSendForJob: (
    job: DashboardJob,
    selectedUserId: DashboardSelectedUser,
    now: Date = new Date(),
  ): number => {
    const viewUserId = dashboard.viewUserForJob(job, selectedUserId);
    return job.candidacies.filter(
      (c) =>
        dashboard.candidacyBelongsToView(c, viewUserId) &&
        dashboard.isCommsToSend(
          c.status,
          c.contactsAttempted,
          c.nextActionAt,
          now,
        ),
    ).length;
  },

  // Applied candidacies the review queue opens with. Ones waiting on a Stevie
  // call need no decision yet, so they stay out — same as the pipeline's
  // Review all. The Applied count still includes them.
  reviewableAppliedForJob: (
    job: DashboardJob,
    selectedUserId: DashboardSelectedUser,
  ): DashboardJob["candidacies"] => {
    const viewUserId = dashboard.viewUserForJob(job, selectedUserId);
    return job.candidacies.filter(
      (c) =>
        c.status === CandidacyStatus.APPLIED &&
        !stevieRoute.isAwaitingCallByMarks(c) &&
        dashboard.candidacyBelongsToView(c, viewUserId),
    );
  },

  countCandidaciesInStatusesForJob: (
    job: DashboardJob,
    statuses: CandidacyStatus[],
    selectedUserId: DashboardSelectedUser,
  ): number => {
    const set = new Set(statuses);
    const viewUserId = dashboard.viewUserForJob(job, selectedUserId);
    return job.candidacies.filter(
      (c) =>
        set.has(c.status) && dashboard.candidacyBelongsToView(c, viewUserId),
    ).length;
  },

  countCandidaciesInStatuses: (
    data: DashboardData | undefined,
    statuses: CandidacyStatus[],
    selectedUserId: DashboardSelectedUser,
  ): number => {
    if (!data) return 0;
    return data.jobs.reduce(
      (acc, job) =>
        acc +
        dashboard.countCandidaciesInStatusesForJob(
          job,
          statuses,
          selectedUserId,
        ),
      0,
    );
  },

  // Pulses arrive newest-first with one entry per staffer.
  latestPulse: (job: DashboardJob): DashboardJob["pulses"][number] | null =>
    job.pulses[0] ?? null,

  latestPulseForUser: (
    job: DashboardJob,
    userId: number,
  ): DashboardJob["pulses"][number] | null =>
    job.pulses.find((pulse) => pulse.userId === userId) ?? null,

  // Applied count the selected staffer had at their last visit, for their delta.
  // Individual views only — the All view has no per-staffer anchor and hides it.
  commsToSendSnapshotForView: (
    job: DashboardJob,
    selectedUserId: DashboardSelectedUser,
  ): number => {
    if (selectedUserId === ALL_USERS_KEY) return 0;
    return (
      job.appliedSnapshotsByUser.find((s) => s.userId === selectedUserId)
        ?.commsToSendCount ?? 0
    );
  },

  appliedSnapshotForView: (
    job: DashboardJob,
    selectedUserId: DashboardSelectedUser,
  ): number => {
    if (selectedUserId === ALL_USERS_KEY) return 0;
    return (
      job.appliedSnapshotsByUser.find((s) => s.userId === selectedUserId)
        ?.appliedCount ?? 0
    );
  },

  // "Comms to send" predicate: a comm is due. Shortlist is unconditional —
  // nobody sits in shortlist without a comm owed, so neither the contact-attempt
  // cap nor the next-action timer applies there. The other three engagement
  // stages must be under the cap with an elapsed timer. Agent-active candidacies
  // count too — the agent communicating doesn't mean a human comm isn't due.
  isCommsToSend: (
    status: CandidacyStatus,
    contactsAttempted: number | null,
    nextActionAt: string | null,
    now: Date = new Date(),
  ): boolean => {
    if (status === CandidacyStatus.SHORTLIST) return true;
    return (
      COMMS_STATUSES.includes(status) &&
      (contactsAttempted ?? 0) < MAX_CONTACTS_ATTEMPTED &&
      nextActionAt !== null &&
      new Date(nextActionAt) <= now
    );
  },

  // Delta vs the last-saved snapshot. If no snapshot, default last value to 0
  getDelta: (current: number, last: number | undefined): number =>
    Math.max(0, current - (last ?? 0)),

  // Locate a candidacy + the rest of its job's candidacies in whichever cache
  // currently has them: dashboard rows (when viewing /dashboard) or
  // candidacies-minimal (when on a job pipeline).
  findCandidacyJobContext: (
    queryClient: QueryClient,
    candidacyId: string,
  ): {
    jobId: string;
    candidacies: SnapshotCandidacy[];
    candidacy: SnapshotCandidacy;
  } | null => {
    const dashboardEntries = queryClient.getQueriesData<DashboardData>({
      queryKey: ["dashboard"],
    });
    for (const [, data] of dashboardEntries) {
      if (!data) continue;
      for (const job of data.jobs) {
        const candidacy = job.candidacies.find((c) => c.id === candidacyId);
        if (candidacy) {
          return { jobId: job.id, candidacies: job.candidacies, candidacy };
        }
      }
    }
    const minimalEntries = queryClient.getQueriesData<MinimalCandidacy[]>({
      queryKey: ["candidacies-minimal"],
    });
    for (const [key, data] of minimalEntries) {
      if (!Array.isArray(data)) continue;
      if (!data.some((c) => c.id === candidacyId)) continue;
      const [, , jobId] = key as [string, string, string];
      const candidacies: SnapshotCandidacy[] = data.map((c) => ({
        id: c.id,
        status: c.lastCandidacyStatusChange.toStatus,
        contactsAttempted: c.contactsAttempted,
        nextActionAt: c.nextActionAt,
        stafferAgentActive: c.stafferAgentActive,
      }));
      const candidacy = candidacies.find((c) => c.id === candidacyId);
      if (!candidacy) continue;
      return { jobId, candidacies, candidacy };
    }
    return null;
  },

  countAppliedExcluding: (
    candidacies: SnapshotCandidacy[],
    excludeId: string,
  ): number =>
    candidacies.filter(
      (c) => c.id !== excludeId && c.status === CandidacyStatus.APPLIED,
    ).length,

  // Whether a column's first click should sort ascending.
  leadsAscending: (key: DashboardSortKey): boolean =>
    ASC_LEADING_SORT_KEYS.includes(key),

  getNextSortState: (
    prev: DashboardSortState | null,
    key: DashboardSortKey,
  ): DashboardSortState | null =>
    cycleColumnSort(prev, key, dashboard.leadsAscending),

  // Per-key value extractors. `null` means "missing" — those rows always sink
  // to the end of the list regardless of sort direction.
  getSortValue: (
    job: DashboardJob,
    key: DashboardSortKey,
    userNameMap: Record<number | string, string>,
    selectedUserId: DashboardSelectedUser,
  ): string | number | null => {
    switch (key) {
      case "jobs":
        return job.title.toLowerCase();
      case "applied":
        return dashboard.countCandidaciesInStatusesForJob(
          job,
          [CandidacyStatus.APPLIED],
          selectedUserId,
        );
      case "commsToSend":
        return dashboard.countCommsToSendForJob(job, selectedUserId);
      case "priority":
        return job.priority === null ? null : PRIORITY_RANK[job.priority];
      case "difficulty":
        return job.difficulty === null ? null : DIFFICULTY_RANK[job.difficulty];
      case "bottleneck":
        return job.bottleneck;
      case "staffer": {
        if (job.ownerUserIds.length === 0) return null;
        return userNameMap[job.ownerUserIds[0]].toLowerCase();
      }
      case "submissions":
        return dashboard.countCandidaciesInStatusesForJob(
          job,
          SUBMISSION_STATUSES,
          selectedUserId,
        );
      case "finalists":
        return dashboard.countCandidaciesInStatusesForJob(
          job,
          FINALIST_STATUSES,
          selectedUserId,
        );
      case "ctd":
        return job.ctdReqs;
      case "ctdRev":
        return job.ctdReqs === null || job.estimatedRevenueAmount === null
          ? null
          : job.ctdReqs * job.estimatedRevenueAmount;
      case "revenue":
        return job.estimatedRevenueAmount;
    }
  },

  // Pinned jobs always float to the top; the active sort (if any) orders within each group.
  sortJobs: (
    jobs: DashboardJob[],
    sort: DashboardSortState | null,
    userNameMap: Record<number | string, string>,
    pinnedJobIds: Set<string>,
    selectedUserId: DashboardSelectedUser,
  ): DashboardJob[] => {
    const directionMultiplier = sort?.direction === SortOrder.Asc ? 1 : -1;

    const compareBySort = (a: DashboardJob, b: DashboardJob): number => {
      if (!sort) return 0;
      const aVal = dashboard.getSortValue(
        a,
        sort.key,
        userNameMap,
        selectedUserId,
      );
      const bVal = dashboard.getSortValue(
        b,
        sort.key,
        userNameMap,
        selectedUserId,
      );
      if (aVal === null && bVal === null) return a.id.localeCompare(b.id);
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (aVal < bVal) return -1 * directionMultiplier;
      if (aVal > bVal) return 1 * directionMultiplier;
      return a.id.localeCompare(b.id);
    };

    const pinRank = (job: DashboardJob) => (pinnedJobIds.has(job.id) ? 0 : 1);
    // Paused, covered, and draft jobs sink to bottom, unless they're pinned
    const pausedRank = (job: DashboardJob) =>
      jobsLib.isMutedOnDashboard(job.status, job.pausedAt) &&
      !pinnedJobIds.has(job.id)
        ? 1
        : 0;

    return [...jobs].sort((a, b) => {
      const pinDiff = pinRank(a) - pinRank(b);
      if (pinDiff !== 0) return pinDiff;
      const pausedDiff = pausedRank(a) - pausedRank(b);
      if (pausedDiff !== 0) return pausedDiff;
      return compareBySort(a, b);
    });
  },
};
